import React, { useEffect, useRef } from 'react';
import { X, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';

/**
 * Premium ConfirmDialog — glassmorphic modal with smooth animations.
 * Supports multiple types (danger, warning, info, success) with themed icons.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
}) {
  const dialogRef = useRef(null);

  // ESC key handler + focus trap
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    // Focus the dialog for accessibility
    setTimeout(() => dialogRef.current?.focus(), 50);
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onCancel]);

  if (!open) return null;

  // Type configurations — icon, color, gradient
  const typeConfig = {
    danger: {
      Icon: AlertTriangle,
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.12)',
      glow: 'rgba(239, 68, 68, 0.25)',
      gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
    },
    warning: {
      Icon: AlertCircle,
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.12)',
      glow: 'rgba(245, 158, 11, 0.25)',
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    },
    info: {
      Icon: Info,
      color: 'var(--accent-tertiary, #3b82f6)',
      bg: 'rgba(59, 130, 246, 0.12)',
      glow: 'rgba(59, 130, 246, 0.25)',
      gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    },
    success: {
      Icon: CheckCircle,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      glow: 'rgba(16, 185, 129, 0.25)',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
    },
  };
  const config = typeConfig[type] || typeConfig.danger;
  const { Icon, color, bg, glow, gradient } = config;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4 animate-fade-in"
      style={{
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full max-w-[440px] max-h-[90vh] overflow-y-auto rounded-2xl animate-scale-in"
        style={{
          background: 'var(--bg-elevated, #1a1e38)',
          border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
          boxShadow: `0 24px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.04), 0 0 60px ${glow}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top gradient accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl"
          style={{ background: gradient }}
        />

        {/* Ambient glow — top corner */}
        <div
          className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-50 pointer-events-none"
          style={{ background: glow }}
        />

        <div className="relative p-6">
          {/* Header — icon + title + close */}
          <div className="flex items-start gap-4 mb-4">
            <div
              className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{
                background: bg,
                boxShadow: `0 0 20px ${glow}`,
              }}
            >
              <Icon size={22} style={{ color }} />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h3
                id="confirm-dialog-title"
                className="font-display text-lg font-bold text-[var(--text-primary, #e6edf3)] leading-tight"
              >
                {title}
              </h3>
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 p-1.5 rounded-lg text-[var(--text-dim, #484f58)] hover:text-[var(--text-primary, #e6edf3)] hover:bg-white/5 transition-all"
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
          </div>

          {/* Message */}
          <p className="text-sm text-[var(--text-secondary, #8b949e)] leading-relaxed mb-6 ml-15">
            {message}
          </p>

          {/* Action buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-[var(--text-secondary, #8b949e)] hover:text-[var(--text-primary, #e6edf3)] transition-all duration-200"
              style={{
                background: 'var(--bg-card, rgba(22, 27, 34, 0.6))',
                border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-glow, rgba(255,255,255,0.15))';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color, rgba(255,255,255,0.06))';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200"
              style={{
                background: gradient,
                boxShadow: `0 4px 16px ${glow}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${glow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 16px ${glow}`;
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
